import { useRef } from 'react';
import { ToxnotServiceClient } from '../../../../store/ToxnotServiceClient';
import { UploadedFile } from '../types';
import { IStringLocalizer } from '../../../../utilities/IStringLocalizer';
import { localizeFormatString } from './useLocalizer';

interface UseFileUploadOptions {
    uploadedFiles: UploadedFile[];
    hasAIAnswers: boolean;
    onUploadedFilesChange: (files: UploadedFile[]) => void;
    onError?: (errorMessage: string) => void;
    localizer?: IStringLocalizer;
}

/**
 * Custom hook for handling file upload logic
 * 
 * Manages:
 * - File selection and upload
 * - Backend file upload via ToxnotServiceClient
 * - Upload state management
 * - Error handling
 * - Auto-scroll to file list
 */
export const useFileUpload = ({
    uploadedFiles,
    hasAIAnswers,
    onUploadedFilesChange,
    onError,
    localizer
}: UseFileUploadOptions) => {
    const serviceClient = new ToxnotServiceClient();
    const fileListRef = useRef<HTMLDivElement>(null);

    /**
     * Creates file upload items from File objects
     */
    const createFileItems = (files: File[]): UploadedFile[] => {
        return files.map(file => ({
            file,
            id: Math.random().toString(36).substr(2, 9),
            isUploading: true,
            fileName: file.name,
            fileSize: file.size
        }));
    };

    /**
     * Handles file selection and upload
     */
    const uploadFiles = async (files: File[]) => {
        const newFileItems = createFileItems(files);

        // Determine the base files array
        let currentFilesList: UploadedFile[];
        if (hasAIAnswers) {
            // If AI answers exist, replace all files with new ones
            currentFilesList = newFileItems;
        } else {
            // Otherwise, append to existing files
            currentFilesList = [...uploadedFiles, ...newFileItems];
        }

        // Update state with all files (including new ones with uploading status)
        onUploadedFilesChange(currentFilesList);

        // Scroll to file list section after a short delay to ensure DOM is updated
        setTimeout(() => {
            if (fileListRef.current) {
                fileListRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }, 100);

        // Upload files to backend one by one
        for (const fileItem of newFileItems) {
            const uploadResult = await serviceClient.uploadGeneralFile(
                fileItem.file.name,
                '', // description
                0, // type
                false, // isSupplierUpload
                false, // isPublic
                fileItem.file
            );

            if (uploadResult.success && uploadResult.data?.fileId) {
                // Update the specific file with backend ID
                currentFilesList = currentFilesList.map(item =>
                    item.id === fileItem.id
                        ? { ...item, fileId: uploadResult.data!.fileId, isUploading: false }
                        : item
                );
                onUploadedFilesChange(currentFilesList);
            } else {
                // Remove failed file
                currentFilesList = currentFilesList.filter(item => item.id !== fileItem.id);
                onUploadedFilesChange(currentFilesList);
                const errorMessage = localizeFormatString(localizer, "Failed to upload file: {0}", fileItem.file.name);
                if (onError) {
                    onError(errorMessage);
                }
            }
        }
    };

    /**
     * Removes a single file by ID
     */
    const removeFile = async (id: string) => {
        onUploadedFilesChange(uploadedFiles.filter(item => item.id !== id));
    };

    /**
     * Clears all uploaded files
     */
    const clearAllFiles = async () => {
        onUploadedFilesChange([]);
    };

    return {
        uploadFiles,
        removeFile,
        clearAllFiles,
        fileListRef
    };
};
